import { Y200, T200 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';

import type { ProjectStyle } from '../../constants/project';
import type { ThirdPartyEntity } from '../../types';

export type OptionType = {
	label: string;
	value: string;
	iconUrl?: string;
	isStarred?: boolean;
	placeholderColor?: string;
	style?: ProjectStyle;
};

export const getDesignTokenBackgroundColor = (colorCode?: string): string => {
	switch (colorCode) {
		case Y200:
			return token('color.background.accent.yellow.subtle', Y200);
		case T200:
		default:
			return token('color.background.accent.teal.subtle', T200);
	}
};

type ConvertEntityToOptionArg = ThirdPartyEntity & {
	placeholderColor?: string;
};

export const convertEntityToOption = ({
	name,
	id,
	iconUrl,
	isStarred,
	placeholderColor,
	style,
}: ConvertEntityToOptionArg): OptionType => ({
	label: name,
	value: id,
	iconUrl,
	isStarred,
	placeholderColor,
	style,
});

export const convertOptionToEntity = (option: OptionType): ThirdPartyEntity => ({
	name: option.label,
	id: option.value,
	iconUrl: option.iconUrl,
	isStarred: option.isStarred,
	style: option.style,
});

export const getSelectedAndAllOptions = (
	entities: ThirdPartyEntity[] | null,
	selectedEntityId: string | null,
	starredOptionsLabel: string,
	otherOptionsLabel: string,
	placeholderColor?: string,
): {
	selectedOption: OptionType | null;
	options: OptionType[] | { label: string; options: OptionType[] }[];
} => {
	if (!entities) {
		return {
			options: [],
			selectedOption: null,
		};
	}

	let selectedOption: OptionType | null = null;
	const starredOptions: OptionType[] = [];
	const otherOptions: OptionType[] = [];

	entities.forEach((entity) => {
		const option = convertEntityToOption({ ...entity, placeholderColor });
		if (entity.id === selectedEntityId) {
			selectedOption = option;
		}

		if (entity.isStarred) {
			starredOptions.push(option);
		} else {
			otherOptions.push(option);
		}
	});

	if (starredOptions.length > 0) {
		return {
			selectedOption,
			options: [
				{
					label: starredOptionsLabel,
					options: starredOptions,
				},
				{
					label: otherOptionsLabel,
					options: otherOptions,
				},
			],
		};
	}

	return {
		selectedOption,
		options: otherOptions,
	};
};

export const getMultipleSelectedAndAllOptions = (
	entities: ThirdPartyEntity[] | null,
	selectedEntities: ThirdPartyEntity[] | null,
): {
	selectedOptions: OptionType[] | null;
	options: OptionType[] | { label: string; options: OptionType[] }[];
} => {
	if (!entities) {
		return {
			options: [],
			selectedOptions: null,
		};
	}

	const selectedOptions: OptionType[] = [];
	const otherOptions: OptionType[] = [];

	entities.forEach((entity) => {
		const option = convertEntityToOption(entity);
		if (selectedEntities?.find((selectedEntity) => selectedEntity.id === entity.id)) {
			selectedOptions.push(option);
		}
		otherOptions.push(option);
	});

	return {
		selectedOptions,
		options: otherOptions,
	};
};
